/**
 * Helper functions to convert map search state to legacy listing page URL format
 */

import { GeographyTypeEnum } from '@nocowanie/core/enums';

import { MAP_DEFAULTS } from '@app/consts';
import { LegacySortApiMapping, SortApiMapping } from '@app/enums/sort-api-mapping.enum';
import { sortingMap } from '@app/helpers/listing-mapping.helpers';
import { LocationDetailsModel, SearchCriteria, SearchFilters } from '@app/models';

/**
 * Convert string array filters to numeric IDs
 * Filters already come as numeric strings, so we just parse them
 */
const convertStringArrayToNumericIds = (stringValues: string[] = []): number[] => {
    return stringValues.map(value => parseInt(value)).filter(id => !isNaN(id));
};

/**
 * Map reservation features from SSR format to legacy format
 */
const mapReservationFeaturesToLegacy = (reservationFeatures?: string[]): string[] => {
    if (!reservationFeatures?.length) return [];

    const legacyMapping: Record<string, string> = {
        withoutPrepayment: 'brak_przedplaty',
        realtimeBookingModel: 'realtime_booking_model',
        freeCancellation: 'anulowanie_rezerwacji',
    };

    return reservationFeatures.map(feature => legacyMapping[feature] || feature).filter(Boolean);
};

/**
 * Map sorting from SSR format to legacy format
 */
const mapSortingToLegacy = (sort: string): string => {
    if (Object.values(sortingMap).includes(sort as LegacySortApiMapping)) {
        // Fallback - legacy param was passed
        return sort;
    }

    // Map SSR Sorting param to legacy
    return sortingMap[sort as SortApiMapping];
};

/**
 * Convert map search state to legacy listing page URL parameters
 */
export function mapSearchStateToLegacyUrl(
    searchCriteria: Partial<SearchCriteria>,
    filters: Partial<SearchFilters>,
    locationDetails?: LocationDetailsModel | null,
): URLSearchParams {
    const params = new URLSearchParams();
    const isAccommodation = locationDetails?.type === GeographyTypeEnum.accommodation;
    const isPoi = locationDetails?.type === GeographyTypeEnum.poi;

    // in case when accommodation is selected on map page
    // and map back button is clicked - then we redirect to Poland
    // legacy listing with these params - this is the only case we need it
    if (isAccommodation) {
        params.set('command', 'search_location');
    }

    const regionId =
        locationDetails?.type === GeographyTypeEnum.region
            ? locationDetails.details.regionId
            : undefined;

    if (regionId) {
        params.set('region', regionId.toString());
    }

    if (searchCriteria.geoId && !regionId && !isPoi) {
        // fallback for building legacy URL when location type is 'accommodation'
        const geoId = isAccommodation ? MAP_DEFAULTS.GEO_ID : searchCriteria.geoId;
        params.set('geo', geoId);
    }

    if (searchCriteria.location) {
        // fallback for building legacy URL when location type is 'accommodation'
        const location = isAccommodation ? MAP_DEFAULTS.LOCATION : searchCriteria.location;
        params.set('q', location);
    }

    if (searchCriteria.checkInDate) {
        const formattedDate = searchCriteria.checkInDate.includes('T')
            ? searchCriteria.checkInDate.split('T')[0]
            : searchCriteria.checkInDate;
        params.set('data[od]', formattedDate);
    }

    if (searchCriteria.checkOutDate) {
        const formattedDate = searchCriteria.checkOutDate.includes('T')
            ? searchCriteria.checkOutDate.split('T')[0]
            : searchCriteria.checkOutDate;
        params.set('data[do]', formattedDate);
    }

    if (searchCriteria.guests) {
        const { adultsCount, childrenCount, childrenAges } = searchCriteria.guests;

        if (adultsCount !== undefined) {
            params.set('miejsca_dorosli', adultsCount.toString());
        }

        if (childrenCount !== undefined) {
            params.set('miejsca_dzieci', childrenCount.toString());
        }

        if (adultsCount !== undefined && childrenCount !== undefined) {
            params.set('miejsca', (adultsCount + childrenCount).toString());
        }

        if (childrenAges && childrenAges.length > 0) {
            params.set('wiek_dzieci', childrenAges.join(','));
        }
    }

    if (filters.sort) {
        params.set('sortowanie_ota', mapSortingToLegacy(filters.sort));
    }

    if (filters.onlineBooking) {
        params.set('rezerwacja_online', filters.onlineBooking);
    }

    const legacyReservationFeatures = mapReservationFeaturesToLegacy(filters.reservationFeatures);

    legacyReservationFeatures.forEach((feature, index) => {
        params.set(`cechy_rezerwacji[${index}]`, feature);
    });

    const propertyTypeIds = convertStringArrayToNumericIds(filters.propertyType);
    propertyTypeIds.forEach((id, index) => {
        params.set(`kat[${index}]`, id.toString());
    });

    const amenityIds = convertStringArrayToNumericIds(filters.amenities);
    amenityIds.forEach(id => {
        params.set(`pikt[${id}]`, id.toString());
    });

    const provisionIds = convertStringArrayToNumericIds(filters.provision);
    provisionIds.forEach((id, index) => {
        params.set(`wyzywienie[${index}]`, id.toString());
    });

    if (filters.priceFrom !== undefined && filters.priceFrom !== null) {
        params.set('cena[od]', filters.priceFrom.toString());
    }

    if (filters.priceTo !== undefined && filters.priceTo !== null) {
        params.set('cena[do]', filters.priceTo.toString());
    }

    if (filters.rating !== undefined && filters.rating !== null) {
        params.set('ocena', filters.rating.toString());
    }

    if (searchCriteria.flexDates) {
        params.set('flex_dates', searchCriteria.flexDates.toString());
    }

    return params;
}

/**
 * Get the legacy listing page URL with current search state
 */
export function getLegacyListingUrl(
    searchCriteria: Partial<SearchCriteria>,
    filters: Partial<SearchFilters>,
    locationDetails?: LocationDetailsModel | null,
    baseUrl = '/',
): string {
    const params = mapSearchStateToLegacyUrl(searchCriteria, filters, locationDetails);
    const isAccommodation = locationDetails?.type === GeographyTypeEnum.accommodation;

    if (!isAccommodation && locationDetails?.path) {
        baseUrl = locationDetails.path;
    }

    return `${baseUrl}?${params.toString()}`;
}
